// S3 (v0.14.0) — Map view: react-leaflet over OpenStreetMap.
//
// Pins are coloured by compliance_score. Click a pin to see the
// entity name, score, total cameras, and a link back to the entity
// browser. Filter to a single entity type via the dropdown.
//
// React Leaflet 4 needs the Leaflet CSS loaded once; we import it
// here so the page is self-contained.

import { useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import {
  MapContainer,
  Marker,
  Popup,
  TileLayer,
  useMap,
} from "react-leaflet";
import { RefreshCwIcon } from "lucide-react";

import { useEntityTypes, useMapEntities, type MapEntityRow } from "@/api/entities";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { QueryState } from "@/components/QueryState";
import { useState } from "react";


// Leaflet's default marker icon URLs are baked into the bundler's
// module path, which Vite doesn't resolve correctly. Override with a
// programmatic divIcon per pin tone so we don't ship the PNGs at all.
function pinIcon(tone: "emerald" | "amber" | "red" | "grey"): L.DivIcon {
  const colors: Record<typeof tone, string> = {
    emerald: "#10b981",
    amber: "#f59e0b",
    red: "#ef4444",
    grey: "#9ca3af",
  } as Record<typeof tone, string>;
  const fill = colors[tone];
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="22" height="32" viewBox="0 0 22 32">
      <path d="M11 0 C5 0 0 5 0 11 c0 8 11 21 11 21 s11-13 11-21 c0-6-5-11-11-11 z"
            fill="${fill}" stroke="#1b2a3b" stroke-width="1.5"/>
      <circle cx="11" cy="11" r="4" fill="white"/>
    </svg>
  `;
  return L.divIcon({
    className: "",
    html: svg,
    iconSize: [22, 32],
    iconAnchor: [11, 32],
    popupAnchor: [0, -28],
  });
}


function toneOf(score: number | null): "emerald" | "amber" | "red" | "grey" {
  if (score === null) return "grey";
  if (score >= 80) return "emerald";
  if (score >= 50) return "amber";
  return "red";
}


function FitBounds({ entities }: { entities: MapEntityRow[] }) {
  const map = useMap();
  useEffect(() => {
    if (entities.length === 0) return;
    const bounds = L.latLngBounds(
      entities.map((e) => [e.gps_lat, e.gps_lng] as [number, number]),
    );
    if (bounds.isValid()) {
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 11 });
    }
  }, [entities, map]);
  return null;
}


export default function MapViewPage() {
  const [typeFilter, setTypeFilter] = useState<string>("");
  const { data: types = [] } = useEntityTypes();
  // A MAP IS THE WORST SURFACE FOR A SILENT EMPTY. `data: entities = []`
  // defaults a failure to zero markers, and the header then states
  // "0 entities on map · 0 cameras" over an empty India — a confident,
  // quantified claim about the estate, assembled entirely from a request that
  // failed. There is no isEmpty here: a genuinely empty map still gets its
  // header and its "0 entities", which is a true statement once the fetch
  // succeeded. Only the failure case needs saying.
  const {
    data: entities = [],
    refetch,
    isFetching,
    isLoading,
    isError,
    error,
  } = useMapEntities(typeFilter ? { entity_type_id: typeFilter } : undefined);

  const totals = useMemo(() => {
    let online = 0;
    let offline = 0;
    let cams = 0;
    for (const e of entities) {
      online += (e.health.total_cameras ?? 0) - (e.health.cameras_offline ?? 0);
      offline += e.health.cameras_offline ?? 0;
      cams += e.health.total_cameras ?? 0;
    }
    return { online, offline, cams };
  }, [entities]);

  return (
    <div className="flex h-[calc(100vh-72px)] flex-col gap-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Entity Map</h1>
          <p className="text-sm text-muted-foreground">
            {entities.length} entit{entities.length === 1 ? "y" : "ies"} on map ·{" "}
            {totals.cams} camera{totals.cams === 1 ? "" : "s"}
            {totals.cams > 0 && (
              <>
                {" · "}
                <span className="text-emerald-600">{totals.online} online</span>
                {" / "}
                <span className="text-red-600">{totals.offline} offline</span>
              </>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="rounded border border-border bg-background px-2 py-1 text-xs"
          >
            <option value="">All types</option>
            {types.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
          <Button
            size="sm"
            variant="outline"
            onClick={() => refetch()}
            disabled={isFetching}
          >
            <RefreshCwIcon
              className={`mr-1 h-3.5 w-3.5 ${isFetching ? "animate-spin" : ""}`}
            />
            Refresh
          </Button>
        </div>
      </div>

      <QueryState
        isLoading={isLoading}
        isError={isError}
        error={error}
        label="the entity map"
        onRetry={() => void refetch()}
      >
      <Card className="flex flex-1 flex-col overflow-hidden">
        <CardContent className="flex-1 p-0">
          <MapContainer
            center={[20.5937, 78.9629]} // India default
            zoom={5}
            scrollWheelZoom
            style={{ height: "100%", width: "100%" }}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <FitBounds entities={entities} />
            {entities.map((e) => (
              <Marker
                key={e.id}
                position={[e.gps_lat, e.gps_lng]}
                icon={pinIcon(toneOf(e.health.compliance_score))}
              >
                <Popup>
                  <div className="space-y-1 text-xs">
                    <p className="text-sm font-semibold">{e.name}</p>
                    {e.code && (
                      <p className="font-mono text-[10px] text-muted-foreground">
                        {e.code}
                      </p>
                    )}
                    {e.entity_type_name && (
                      <p className="text-muted-foreground">
                        {e.entity_type_name}
                      </p>
                    )}
                    <p>
                      <strong>Cameras:</strong> {e.health.total_cameras} ·
                      offline {e.health.cameras_offline}
                    </p>
                    <p>
                      <strong>Compliance:</strong>{" "}
                      {e.health.compliance_score === null
                        ? "—"
                        : `${e.health.compliance_score}%`}
                    </p>
                    <Link
                      to={`/entities?selected=${e.id}`}
                      className="text-primary underline"
                    >
                      View in Entity Browser →
                    </Link>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </CardContent>
      </Card>
      </QueryState>
    </div>
  );
}
